/**
 * The agency CRM's shared rules for the browser: pipeline stages (the same list as
 * utils/crmStages.js on the server), money, names, and the booking calendar's
 * month grid and slot grouping. No imports and only erasable types, so node --test
 * loads this file directly (test/crm.test.js).
 */

export type StageId = "new_lead" | "qualified" | "call_booked" | "follow_up" | "proposal" | "won" | "lost" | "dq";
export type Stage = { id: StageId; label: string; open: boolean };

export const STAGES: Stage[] = [
  { id: "new_lead", label: "New lead", open: true },
  { id: "qualified", label: "Qualified", open: true },
  { id: "call_booked", label: "Call booked", open: true },
  { id: "follow_up", label: "Follow-up", open: true },
  { id: "proposal", label: "Proposal sent", open: true },
  { id: "won", label: "Won", open: false },
  { id: "lost", label: "Lost", open: false },
  { id: "dq", label: "Not a fit", open: false },
];

/** The board shows every open stage plus Won; Lost and Not a fit live on the Closed tab. */
export const BOARD_STAGES = STAGES.filter((s) => s.open || s.id === "won");
export const CLOSED_STAGES = STAGES.filter((s) => s.id === "lost" || s.id === "dq");
export const stageLabel = (id: string) => STAGES.find((s) => s.id === id)?.label ?? id;

/** "$12,500" from cents; "" for nothing. */
export function money(cents: number | string | null | undefined): string {
  const n = Number(cents);
  if (!Number.isFinite(n) || n === 0) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n / 100);
}

/** "12,500", "$12,500.50" or "12.5k" typed by a person, as cents; null when it isn't a number. */
export function parseMoney(input: string): number | null {
  const cleaned = String(input).replace(/[$,\s]/g, "").toLowerCase();
  if (!cleaned) return 0;
  const m = /^(\d+(?:\.\d*)?|\.\d+)([km]?)$/.exec(cleaned);
  if (!m) return null;
  const scale = m[2] === "k" ? 1000 : m[2] === "m" ? 1000000 : 1;
  const dollars = Number(m[1]) * scale;
  // More than two decimals only makes sense with k/m ("12.55k"), never for plain cents.
  if (!m[2] && /\.\d{3,}$/.test(m[1])) return null;
  // The server's ceiling ($10 billion): anything bigger is a typo.
  if (dollars * 100 > 1e12) return null;
  return Math.round(dollars * 100);
}

/** Cents as an amount field shows them: "12,500", "12,500.50", "" for nothing. */
export function moneyField(cents: number | string | null | undefined): string {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: n % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(n / 100);
}

/**
 * Commas put in while someone types an amount ("12500" → "12,500"), leaving what
 * they are still typing alone: a trailing ".", decimals, a k/m shorthand.
 */
export function typingMoney(input: string): string {
  const raw = String(input).replace(/[^\d.kKmM]/g, "");
  const m = /^(\d*)(\.\d{0,2})?([kKmM]?)/.exec(raw);
  if (!m) return "";
  const whole = m[1].replace(/^0+(?=\d)/, "");
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${m[2] || ""}${m[3] || ""}`;
}

/** How long something has sat since `iso`: "4m", "3h", "2d", "5w". */
export function sinceShort(iso: string | null | undefined, now = Date.now()): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.floor((now - t) / 60000));
  if (min < 60) return `${min}m`;
  if (min < 60 * 24) return `${Math.floor(min / 60)}h`;
  const days = Math.floor(min / (60 * 24));
  return days < 14 ? `${days}d` : `${Math.floor(days / 7)}w`;
}

/**
 * A phone number with the picked country code in front, unless the person already
 * typed their own "+" code ("+44 20 7946 0958" stays as typed, never "+1 +44 ...").
 * "" when there are no digits.
 */
export function withDialCode(code: string, typed: string): string {
  const phone = String(typed || "").trim();
  if (!phone.replace(/\D/g, "")) return "";
  return phone.startsWith("+") ? phone : `${code} ${phone}`;
}

/**
 * The ids of one board column in their new order after a card is placed before
 * `before` (or at the end). Works on every deal, not just the ones a search shows,
 * so saving the order never leaves hidden cards out.
 */
export function columnOrder<T extends { id: number; stage: string }>(all: T[], dealId: number, stage: string, before: number | null): number[] {
  const column = all.filter((d) => d.stage === stage && d.id !== dealId).map((d) => d.id);
  const at = before === null ? -1 : column.indexOf(before);
  column.splice(at === -1 ? column.length : at, 0, dealId);
  return column;
}

export type ContactName = { first_name?: string | null; last_name?: string | null; email?: string | null; company?: string | null };
/** The name to show for a contact: their name, else their email, else their company. */
export function contactName(c: ContactName): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || c.company || "Unnamed contact";
}

/* Booking calendar: month grid (calendar cells, not instants, so plain UTC maths). */

export type GridDay = { key: string; y: number; m: number; d: number; inMonth: boolean };
export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const dayKey = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export function monthLabel(y: number, m: number): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Six Monday-first weeks covering the month, so the grid never changes height. */
export function monthMatrix(y: number, m: number): GridDay[][] {
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const cursor = new Date(Date.UTC(y, m - 1, 1 - lead));
  const weeks: GridDay[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: GridDay[] = [];
    for (let i = 0; i < 7; i++) {
      const cy = cursor.getUTCFullYear();
      const cm = cursor.getUTCMonth() + 1;
      const cd = cursor.getUTCDate();
      row.push({ key: dayKey(cy, cm, cd), y: cy, m: cm, d: cd, inMonth: cm === m });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

export function addMonths(y: number, m: number, delta: number): { y: number; m: number } {
  const t = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
}

const keyFormat = (tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });

/** "2:00 PM": the one way a time reads in the CRM (Intl's narrow space made a plain one). */
function clock(d: Date, tz: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(d).replace(/\s+/g, " ");
}

/** The YYYY-MM-DD a slot falls on for the viewer. */
export const slotDay = (iso: string, tz: string) => keyFormat(tz).format(new Date(iso));

/** Slot starts grouped by the viewer's calendar day, with "9:30 AM" labels. */
export function slotsByDay(slots: string[], tz: string): Map<string, { iso: string; label: string }[]> {
  const fmt = keyFormat(tz);
  const map = new Map<string, { iso: string; label: string }[]>();
  for (const iso of [...slots].sort()) {
    const key = fmt.format(new Date(iso));
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({ iso, label: clock(new Date(iso), tz) });
  }
  return map;
}

/** "Tuesday, October 7 at 2:00 PM" in a time zone. Every booking time on screen uses it; staff screens add "ET". */
export function whenLabel(iso: string, tz: string): string {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric" }).format(d);
  return `${day} at ${clock(d, tz)}`;
}

/** "Mon, Oct 5, 3:00 PM ET": the short form for cards, tables and lists, on the agency's clock. */
export function shortWhen(iso: string, tz = "America/New_York"): string {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(d);
  return `${day}, ${clock(d, tz)}${tz === "America/New_York" ? " ET" : ""}`;
}

/** A time in text the server wrote ("10:00 AM EDT") the way the CRM writes it ("10:00 AM ET"). */
export const easternText = (text: string) => text.replace(/\b(\d{1,2}:\d{2}\s?[AP]M)\s+E[DS]T\b/g, "$1 ET");

const CALL_STATUS: Record<string, string> = { booked: "Booked", showed: "Showed", no_show: "No-show", cancelled: "Cancelled" };
/**
 * A call's status in words, the same in the contact drawer and on Bookings: a call that
 * has ended with nobody marking it showed or no-show is "Not marked", not "Booked".
 */
export function callStatusLabel(status: string, endsAt: string | null | undefined, now = Date.now()): string {
  if (status === "booked" && endsAt && new Date(endsAt).getTime() <= now) return "Not marked";
  return CALL_STATUS[status] ?? status;
}

/** A call moved to a new time: the old one is cancelled with "Moved to ..." (routes/crm.js, Reschedule). */
export const wasRescheduled = (b: { status: string; cancel_reason?: string | null }) => b.status === "cancelled" && /^Moved to /.test(b.cancel_reason || "");

/** A short readable zone name: "America/New_York" → "New York". */
export const tzLabel = (tz: string) => (tz.split("/").pop() || tz).replace(/_/g, " ");

/** The viewer's own IANA zone, or Eastern. */
export function viewerTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}
